import React, { useState, useEffect } from 'react';
import './ContractEditor.css';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { Horse, TemplateSection, CompanySettings } from './types';
import { loadHorsesData } from './horsesData';
import { saveHorseList, fetchTemplateSections, storeContractPDF, fetchCompanySettings } from './api';
import ContractDocumentList from './ContractDocumentList';
import ContractUploadDialog from './ContractUploadDialog';

(pdfMake as any).vfs = pdfFonts;

/**
 * Parse template section content into pdfmake structures.
 * Convention:
 * - Paragraphs separated by blank lines become numbered sub-items (ol)
 * - Lines starting with "- " are bullet items (ul), grouped with preceding paragraph
 * - Single paragraph = simple text (no ol wrapper needed, but we wrap for consistency)
 */
function parseSectionContent(content: string): any {
  const paragraphs = content.split(/\n\n+/);
  const items: any[] = [];

  for (const para of paragraphs) {
    const lines = para.split('\n');
    const mainLines: string[] = [];
    const bulletItems: string[] = [];

    for (const line of lines) {
      if (line.startsWith('- ')) {
        bulletItems.push(line.substring(2));
      } else {
        mainLines.push(line);
      }
    }

    const mainText = mainLines.join('\n');

    if (bulletItems.length > 0) {
      // Paragraph with bullet list
      items.push([mainText, { ul: bulletItems }]);
    } else {
      // Plain paragraph
      items.push(mainText);
    }
  }

  // Single paragraph: return just the text (no numbered list needed)
  if (items.length === 1 && typeof items[0] === 'string') {
    return items[0];
  }

  // Multiple items: return as ordered list
  return { ol: items };
}

// Get base path for API calls (app is at domain root)
const getBasePath = () => {
  return '/databaseOps/';
};

interface ContractData {
  name: string;
  strasse: string;
  plzOrt: string;
  telefon: string;
  boxenMiete: number;
  pferdeName: string;
  aufschlag: number;
  tagMietbeginn: string;
  tagMietende: string;
  tagDesVertrages: string;
  grosseBox: boolean;
  zeitUnbestimmt: boolean;
}

const ContractEditor: React.FC = () => {
  const [formData, setFormData] = useState<ContractData>({
    name: '',
    strasse: '',
    plzOrt: '',
    telefon: '',
    boxenMiete: 0,
    pferdeName: '',
    aufschlag: 0,
    tagMietbeginn: '',
    tagMietende: '',
    tagDesVertrages: new Date().toISOString().split('T')[0],
    grosseBox: false,
    zeitUnbestimmt: false
  });
  const [nextId, setNextId] = useState(43);
  const [allHorses, setAllHorses] = useState<Horse[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [templateSections, setTemplateSections] = useState<TemplateSection[]>([]);
  const [company, setCompany] = useState<CompanySettings>({});
  const [lastSavedContractId, setLastSavedContractId] = useState<string | null>(null);
  const [docRefresh, setDocRefresh] = useState(0);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Load horse data and template sections
  useEffect(() => {
    loadHorsesData()
      .then(({ horses, nextId }) => {
        setAllHorses(horses);
        setNextId(nextId);
      })
      .catch((error) => {
        console.error('Failed to load horse data:', error);
      });

    fetchTemplateSections()
      .then(sections => setTemplateSections(sections))
      .catch(error => console.error('Failed to load template:', error));

    fetchCompanySettings()
      .then(settings => setCompany(settings))
      .catch(error => console.error('Failed to load company settings:', error));
  }, []);

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  const formatNumber = (num: number, decimals: number = 2): string => {
    return num.toFixed(decimals).replace('.', ',');
  };

  const handleInputChange = (field: keyof ContractData, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const saveHorse = async () => {
    if (!formData.pferdeName.trim()) {
      alert('Bitte geben Sie einen Pferdenamen ein.');
      return;
    }

    setSaveStatus('saving');

    const horseId = `pony/${nextId}`;

    // Create new horse
    const newHorse: Horse = {
      id: horseId,
      name: formData.pferdeName,
      owner: formData.name,  // Keep for backward compatibility
      phone: formData.telefon,  // Keep for backward compatibility
      weight: 0,
      hidden: false,  // Will be managed via contract
      early: 'nichts',
      late: 'nichts',
      bricks: 'keine',
      contractDate: formData.tagDesVertrages  // Keep for backward compatibility
    };

    try {
      // Step 1: Save the horse
      const updatedHorses = [...allHorses, newHorse];
      const horseData = {
        ponyNextID: nextId + 1,
        ponys: updatedHorses.reduce((acc, horse) => {
          acc[horse.id] = horse;
          return acc;
        }, {} as Record<string, Horse>)
      };

      await saveHorseList(horseData);

      // Step 2: Create the contract
      const contractData = {
        horse_id: horseId,
        owner_name: formData.name,
        owner_street: formData.strasse,
        owner_city: formData.plzOrt,
        owner_phone: formData.telefon,
        box_rent: formData.boxenMiete,
        rent_surcharge: formData.aufschlag,
        contract_date: formData.tagDesVertrages,
        start_date: formData.tagMietbeginn,
        end_date: formData.zeitUnbestimmt ? null : formData.tagMietende,
        indefinite: formData.zeitUnbestimmt,
        large_box: formData.grosseBox,
        hidden: false,
        active: true
      };

      const contractResponse = await fetch(`${getBasePath()}saveContract.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contractData)
      });

      const contractResult = await contractResponse.json();
      if (!contractResponse.ok || !contractResult.success) {
        throw new Error(contractResult.error || 'Failed to save contract');
      }

      console.log('Save successful:', contractResult);
      setSaveStatus('saved');
      setLastSavedContractId(contractResult.contract_id);
      setNextId(nextId + 1);
      setTimeout(() => setSaveStatus('idle'), 2000);
      alert(`Pferd "${formData.pferdeName}" und Vertrag wurden erfolgreich gespeichert!`);

    } catch (error: any) {
      console.error('Error saving:', error);
      setSaveStatus('error');
      alert(`Fehler beim Speichern:\n\n${error.message}\n\nDetails siehe Browser-Konsole (F12)`);
      setTimeout(() => setSaveStatus('idle'), 5000);
    }
  };

  // Resolve placeholders in template content with form data
  const resolvePlaceholders = (text: string): string => {
    const mm = formatNumber(formData.boxenMiete);
    const ma = formatNumber(formData.boxenMiete + formData.aufschlag);

    const rentText = formData.zeitUnbestimmt
      ? `Der monatliche Pensionspreis beträgt ${mm} €.`
      : `Der monatliche Pensionspreis beträgt in den ersten drei Monaten ${ma} €. Danach beträgt er ${mm} €.`;

    const rentDurationText = formData.zeitUnbestimmt
      ? ' und läuft auf unbestimmte Zeit.'
      : ' und endet am ' + formatDate(formData.tagMietende);

    const co = (key: string) => company[key]?.value || '';

    const replacements: Record<string, string> = {
      '{horse_name}': formData.pferdeName,
      '{owner_name}': formData.name,
      '{owner_street}': formData.strasse,
      '{owner_city}': formData.plzOrt,
      '{box_size}': formData.grosseBox ? '3,50m' : '3m',
      '{start_date}': formatDate(formData.tagMietbeginn),
      '{end_date}': formData.zeitUnbestimmt ? 'unbestimmte Zeit' : formatDate(formData.tagMietende),
      '{box_rent}': mm,
      '{rent_surcharge}': formatNumber(formData.aufschlag),
      '{contract_date}': formatDate(formData.tagDesVertrages),
      '{rent_text}': rentText,
      '{rent_duration_text}': rentDurationText,
      // Company settings
      '{company_name}': co('company_name'),
      '{managing_directors}': co('managing_directors'),
      '{company_street}': co('street'),
      '{company_city}': co('city'),
      '{company_phone}': co('phone'),
      '{company_email}': co('email'),
      '{company_website}': co('website'),
      '{bank_name}': co('bank_name'),
      '{iban}': co('iban'),
      '{bic}': co('bic'),
      '{account_holder}': co('account_holder'),
      '{tax_office}': co('tax_office'),
      '{tax_number}': co('tax_number'),
    };

    let result = text;
    for (const [key, value] of Object.entries(replacements)) {
      result = result.split(key).join(value);
    }
    return result;
  };

  const generatePDF = () => {
    const co = (key: string) => company[key]?.value || '';

    // Build clause list from template sections
    const sections = templateSections.length > 0 ? templateSections : [];

    const clauseList = sections.map(section => {
      const resolvedContent = resolvePlaceholders(section.content);
      const parsedContent = parseSectionContent(resolvedContent);

      return [
        { text: section.title, style: 'subheader' },
        parsedContent
      ];
    });

    const docDefinition: any = {
      footer: function (currentPage: number, pageCount: number) {
        return {
          margin: [60, 0, 0, 0],
          table: {
            widths: ['auto', 'auto', 'auto', 'auto'],
            body: [
              [
                {
                  border: [false, true, false, false],
                  text: `${co('company_name')}\nGeschäftsführer\n${co('managing_directors')}\nTel.: ${co('phone')}`,
                  style: 'companySmall'
                },
                {
                  border: [false, true, false, false],
                  text: `${co('bank_name')}\nIBAN ${co('iban')}\nBIC ${co('bic')}\nEmail: ${co('email')}`,
                  style: 'companySmall'
                },
                {
                  border: [false, true, false, false],
                  text: `${co('tax_office')}\nStnr. ${co('tax_number')}\nweb: ${co('website')}`,
                  style: 'companySmall'
                },
                {
                  border: [false, true, false, false],
                  text: 'Seite ' + currentPage.toString() + ' von ' + pageCount,
                  style: 'companySmall'
                }
              ]
            ]
          },
          layout: {
            hLineColor: 'grey',
          }
        };
      },
      content: [
        {
          text: 'Pferdeeinstellungsvertrag',
          style: 'headline'
        },
        {
          text: [
            'Zwischen\n',
            co('company_name') + '\n',
            co('street') + '\n',
            co('city') + '\n',
            'im Folgenden „Betrieb" genannt\n',
            'und'
          ],
          style: 'Betrieb'
        },
        {
          text: [
            formData.name + '\n',
            formData.strasse + '\n',
            formData.plzOrt + '\n',
            'im Folgenden „Einsteller" genannt',
          ],
          style: 'Betrieb'
        },
        {
          ol: clauseList,
          lineHeight: 1.3
        },
        {
          margin: [0, 15, 0, 50],
          text: 'Fockbek, den ' + formatDate(formData.tagDesVertrages)
        },
        {
          style: 'tableExample',
          table: {
            widths: ['*', 100, '*'],
            body: [
              [
                {
                  border: [false, true, false, false],
                  text: 'für den Betrieb:',
                  alignment: 'center'
                },
                {
                  border: [false, false, false, false],
                  text: '',
                  alignment: 'center'
                },
                {
                  border: [false, true, false, false],
                  text: 'für den Einsteller:',
                  alignment: 'center'
                }
              ]
            ]
          }
        }
      ],
      pageSize: 'A4',
      pageMargins: [50, 40, 50, 85],
      styles: {
        company: {
          color: 'black',
          fontSize: 11
        },
        companySmall: {
          color: 'grey',
          fontSize: 7,
          alignment: 'center'
        },
        headline: {
          bold: true,
          color: 'black',
          fontSize: 22,
          decoration: 'underline',
          margin: [0, 0, 0, 8],
          alignment: 'center'
        },
        subheader: {
          fontSize: 14,
          margin: [0, 8, 0, 8]
        },
        Betrieb: {
          bold: true,
          color: 'black',
          fontSize: 13,
          margin: [10, 8, 10, 8],
          alignment: 'center'
        },
        tableExample: {
          margin: [10, 4, 10, 8],
        }
      }
    };

    const pdfDoc = pdfMake.createPdf(docDefinition);

    // Open in browser
    pdfDoc.open();

    // Also store persistently if a contract was saved
    if (lastSavedContractId) {
      pdfDoc.getBlob((blob: Blob) => {
        const filename = `Vertrag_${formData.pferdeName.replace(/\s+/g, '_')}_${formData.tagDesVertrages}.pdf`;
        storeContractPDF(blob, filename, lastSavedContractId)
          .then(() => {
            console.log('Contract PDF stored successfully');
            setDocRefresh(prev => prev + 1);
          })
          .catch(err => console.error('Failed to store PDF:', err));
      });
    }
  };

  return (
    <div className="contract-editor">
      <header className="header">
        <h2>Einstellervertrag erstellen</h2>
      </header>

      <div className="form-container">
        <form className="contract-form">
          <div className="form-field">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="strasse">Strasse</label>
            <input
              type="text"
              id="strasse"
              value={formData.strasse}
              onChange={(e) => handleInputChange('strasse', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="plzOrt">PLZ / Ort</label>
            <input
              type="text"
              id="plzOrt"
              value={formData.plzOrt}
              onChange={(e) => handleInputChange('plzOrt', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="telefon">Telefon</label>
            <input
              type="text"
              id="telefon"
              value={formData.telefon}
              onChange={(e) => handleInputChange('telefon', e.target.value)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="boxenMiete">Boxenmiete (€)</label>
            <input
              type="number"
              id="boxenMiete"
              value={formData.boxenMiete}
              onChange={(e) => handleInputChange('boxenMiete', parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className="form-field">
            <label htmlFor="pferdeName">Name des Pferdes</label>
            <input
              type="text"
              id="pferdeName"
              value={formData.pferdeName}
              onChange={(e) => handleInputChange('pferdeName', e.target.value)}
            />
          </div>

          {!formData.zeitUnbestimmt && (
            <div className="form-field">
              <label htmlFor="aufschlag">Mietaufschlag (€)</label>
              <input
                type="number"
                id="aufschlag"
                value={formData.aufschlag}
                onChange={(e) => handleInputChange('aufschlag', parseFloat(e.target.value) || 0)}
              />
            </div>
          )}

          <div className="form-field">
            <label htmlFor="tagMietbeginn">Datum des Mietbeginns</label>
            <input
              type="date"
              id="tagMietbeginn"
              value={formData.tagMietbeginn}
              onChange={(e) => handleInputChange('tagMietbeginn', e.target.value)}
            />
          </div>

          {!formData.zeitUnbestimmt && (
            <div className="form-field">
              <label htmlFor="tagMietende">Datum des Mietendes</label>
              <input
                type="date"
                id="tagMietende"
                value={formData.tagMietende}
                onChange={(e) => handleInputChange('tagMietende', e.target.value)}
              />
            </div>
          )}

          <div className="form-field">
            <label htmlFor="tagDesVertrages">Datum des Vertrages</label>
            <input
              type="date"
              id="tagDesVertrages"
              value={formData.tagDesVertrages}
              onChange={(e) => handleInputChange('tagDesVertrages', e.target.value)}
            />
          </div>

          <div className="checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.grosseBox}
                onChange={(e) => handleInputChange('grosseBox', e.target.checked)}
              />
              große Box (3,50m statt 3m)
            </label>
          </div>

          <div className="checkbox-field">
            <label>
              <input
                type="checkbox"
                checked={formData.zeitUnbestimmt}
                onChange={(e) => handleInputChange('zeitUnbestimmt', e.target.checked)}
              />
              unbestimmte Dauer
            </label>
          </div>
        </form>
      </div>

      <footer className="actions">
        {saveStatus !== 'idle' && (
          <div className={`save-status save-status-${saveStatus}`}>
            {saveStatus === 'saving' && '💾 Speichert...'}
            {saveStatus === 'saved' && '✓ Gespeichert!'}
            {saveStatus === 'error' && '⚠ Fehler beim Speichern'}
          </div>
        )}
        <div className="button-group">
          <button className="btn-save" onClick={saveHorse}>
            Pferd speichern
          </button>
          <button className="btn-print" onClick={generatePDF}>
            Vertrag als PDF erstellen
          </button>
          <button className="btn-upload-contract" onClick={() => setShowUploadDialog(true)}>
            Altvertrag hochladen
          </button>
        </div>
      </footer>

      {showUploadDialog && (
        <ContractUploadDialog
          horses={allHorses}
          onClose={() => setShowUploadDialog(false)}
          onUploaded={() => setDocRefresh(prev => prev + 1)}
        />
      )}

      {lastSavedContractId && (
        <div className="documents-section">
          <h3>Gespeicherte Vertragsdokumente</h3>
          <ContractDocumentList contractId={lastSavedContractId} refreshTrigger={docRefresh} />
        </div>
      )}
    </div>
  );
};

export default ContractEditor;
